import { useCallback, useState, useEffect } from 'react';
import { DatePicker } from '@material-ui/pickers';
import { TextField, Grid } from '@material-ui/core';
import { DateTime } from 'luxon';

interface Props {
  disabled?: boolean;
  values?: [DateTime, DateTime];
  onChange?: (values: [DateTime, DateTime]) => void;
}

function DateRangePicker(props: Props): JSX.Element {
  const { values, onChange, disabled } = props;
  const [startDate, setStartDate] = useState<DateTime | null>(
    values?.[0] || null
  );
  const [endDate, setEndDate] = useState<DateTime | null>(values?.[1] || null);
  useEffect(() => {
    if (values) {
      setStartDate(values[0]);
      setEndDate(values[1]);
    }
  }, [values]);

  const updateStartDate = useCallback(
    (newDate: DateTime | null) => {
      if (!newDate) return;

      setStartDate(newDate);
      setEndDate(null);

      if (onChange) {
        onChange([newDate, newDate]);
      }
    },
    [onChange]
  );

  const updateEndDate = useCallback(
    (newDate: DateTime | null) => {
      if (!newDate || !startDate) return;
      if (newDate <= startDate) return;

      setEndDate(newDate);
      if (onChange) {
        onChange([startDate, newDate]);
      }
    },
    [startDate, onChange]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <DatePicker
          disabled={disabled}
          label="Start Date"
          inputFormat="dd/MM/yyyy"
          // disabled past date from now on
          minDate={DateTime.local()}
          value={startDate}
          onChange={updateStartDate}
          renderInput={(props) => (
            <TextField fullWidth {...props} variant="filled" />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <DatePicker
          disabled={disabled || !startDate}
          label="End Date"
          inputFormat="dd/MM/yyyy"
          value={endDate}
          minDate={startDate ? startDate.plus({ days: 1 }) : undefined}
          onChange={updateEndDate}
          renderInput={(props) => (
            <TextField fullWidth {...props} variant="filled" />
          )}
        />
      </Grid>
    </Grid>
  );
}

export default DateRangePicker;
